import { Fragment } from "react";
import Tealium from "@4tn/webx-analytics";
import IconButton from "@common/Buttons/IconButton";
import { BUTTON_SIZE, VARIANT } from "@constants/button";
import { IconEnum } from "@constants/consts";
import { colors } from "@constants/cssVariables";
import { SocialShareItemEnum, socialShareArticleText, socialShareVideoText } from "@constants/socialSharing";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_LABEL, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import Popper from "./Popper";
import * as Styled from "./SocialSharing.styled";

type SocialShareItemType = {
  name: SocialShareItemEnum;
  accessibilityLabel: string;
  icon: IconEnum;
  hasPopper?: boolean;
  clickHandler: (url?: string, isVideo?: boolean) => void;
};

const trackClick = (eventLabel: string, isVideo?: boolean) => {
  Tealium.link({
    event_category: isVideo ? TEALIUM_EVENT_CATEGORY.VIDEO : TEALIUM_EVENT_CATEGORY.ARTICLE,
    event_name: TEALIUM_EVENT_NAME.SHARE_INTERACTION,
    event_label: eventLabel,
  });
};

const getFallbackUrl = () => {
  const [url] = window.location.href.split("?");
  return url;
};

const socialShareItems: SocialShareItemType[] = [
  {
    name: SocialShareItemEnum.WHATSAPP,
    accessibilityLabel: "Deel via Whatsapp",
    icon: IconEnum.WHATSAPP,
    clickHandler: (url, isVideo) => {
      window.open(
        `https://api.whatsapp.com/send?text=${isVideo ? socialShareVideoText : socialShareArticleText} ${
          url || getFallbackUrl()
        }`,
        "_blank"
      );
      trackClick(TEALIUM_EVENT_LABEL.SOCIAL_SHARE_LABELS.WHATSAPP, isVideo);
    },
  },
  {
    name: SocialShareItemEnum.MAIL,
    accessibilityLabel: "Verstuur via e-mail",
    icon: IconEnum.MAIL,
    clickHandler: (url, isVideo) => {
      window.open(
        `mailto:?body=${isVideo ? socialShareVideoText : socialShareArticleText} ${url || getFallbackUrl()}`,
        "_blank"
      );
      trackClick(TEALIUM_EVENT_LABEL.SOCIAL_SHARE_LABELS.EMAIL);
    },
  },
  {
    name: SocialShareItemEnum.FACEBOOK,
    accessibilityLabel: "Deel op Facebook",
    icon: IconEnum.FACEBOOK,
    clickHandler: (url, isVideo) => {
      window.open(`https://www.facebook.com/sharer.php?u=${url || getFallbackUrl()}`, "_blank");
      trackClick(TEALIUM_EVENT_LABEL.SOCIAL_SHARE_LABELS.FACEBOOK, isVideo);
    },
  },
  {
    name: SocialShareItemEnum.TWITTER,
    accessibilityLabel: "Deel op X",
    icon: IconEnum.TWITTER,
    clickHandler: (url, isVideo) => {
      window.open(
        `https://twitter.com/intent/tweet?text=${isVideo ? socialShareVideoText : socialShareArticleText} ${
          url || getFallbackUrl()
        }`,
        "_blank"
      );
      trackClick(TEALIUM_EVENT_LABEL.SOCIAL_SHARE_LABELS.X, isVideo);
    },
  },
  {
    name: SocialShareItemEnum.LINK,
    accessibilityLabel: "Kopieer link",
    icon: IconEnum.LINK,
    hasPopper: true,
    clickHandler: (url, isVideo) => {
      window.navigator.clipboard.writeText(url || getFallbackUrl());
      trackClick(TEALIUM_EVENT_LABEL.SOCIAL_SHARE_LABELS.COPY_LINK, isVideo);
    },
  },
];

interface SocialSharingProps {
  socialTitle?: string;
  url?: string;
  isVideo?: boolean;
}

const SocialSharingItem: React.FC<SocialShareItemType & { url?: string; isVideo?: boolean }> = ({
  hasPopper,
  accessibilityLabel,
  url,
  isVideo,
  icon,
  clickHandler,
}) => {
  const Wrapper = hasPopper ? Popper : Fragment;
  return (
    <Wrapper>
      <IconButton
        icon={icon}
        size={BUTTON_SIZE.MEDIUM}
        variant={VARIANT.PRIMARY}
        color={colors.neutral0}
        aria-label={accessibilityLabel}
        onClick={() => clickHandler(url, isVideo)}
      />
    </Wrapper>
  );
};

const SocialSharing: React.FC<SocialSharingProps> = ({ socialTitle, url, isVideo }) => (
  <Styled.SocialSharingContainer>
    {socialTitle && <Styled.SocialSharingTitle>{socialTitle}</Styled.SocialSharingTitle>}
    <Styled.SocialSharingItemsContainer>
      {socialShareItems.map((item) => (
        <SocialSharingItem key={item.name} {...item} url={url} isVideo={isVideo} />
      ))}
    </Styled.SocialSharingItemsContainer>
  </Styled.SocialSharingContainer>
);

export default SocialSharing;
